import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, FC, Fragment, SetStateAction, useState } from 'react';
import {
  AudienceSet,
  AudienceSetTargetsData,
  AudienceSetTargetType,
  Brand,
  BulkSearchBody,
  ChangeEventType,
} from '../../../../../types';
import { PrimaryButton } from '../../../../Buttons';
import Spinner from '../../../../Spinner';
import AudienceList from './AudienceList';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { fbBulkSearch } from '../../../../../services/ads';
import {
  EDUCATION_STATUSES,
  RELATIONSHIP_STATUSES,
  TARGET_TYPES,
} from '../../../../../utils/helpers/AssetHelpers';
import { HtmlTooltip } from '../../../../Tooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';

interface DetailedAudienceSetFormProps {
  formValues: AudienceSet;
  setFormValues: Dispatch<SetStateAction<AudienceSet>>;
  brand: Brand;
}

const DetailedAudienceSetForm: FC<DetailedAudienceSetFormProps> = ({
  formValues,
  setFormValues,
  brand,
}) => {
  const dispatch = useDispatch();
  const [searchFilters, setSearchFilters] = useState<string[]>(['interests']);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [targets, setTargets] = useState<AudienceSetTargetsData[]>([]);
  const [filteredTargets, setFilteredTargets] = useState<
    AudienceSetTargetsData[]
  >([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [limit, setLimit] = useState<number>(25);

  const handleOnSearchLocation = (e: ChangeEventType) => {
    setSearch(e.target.value);
    let temp: AudienceSetTargetsData[] = [];

    if (e.target.value === '') {
      temp = targets;
    } else {
      temp = targets.filter((target: AudienceSetTargetsData) =>
        target.name.toLowerCase().includes(e.target.value.toLowerCase()),
      );
    }

    setFilteredTargets(temp);
  };

  const handleOnSearch = async () => {
    try {
      if (!searchQuery.replace(/\s/g, '').length) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: 'Please enter a search query',
            type: 'error',
          }),
        );
      }

      setSearchLoading(true);

      let params: BulkSearchBody = {
        type: 'adDetailedTargeting',
        searchQueries: searchQuery.split('\n').filter((value) => value),
        targetingTypes: searchFilters,
      };

      const response = await fbBulkSearch(brand?._id, params);

      let data: AudienceSetTargetsData[] = response.data;

      if (searchFilters.includes('education_statuses')) {
        data = [...data, ...EDUCATION_STATUSES];
      }

      if (searchFilters.includes('relationship_statuses')) {
        data = [...data, ...RELATIONSHIP_STATUSES];
      }

      setTargets(data);
      setFilteredTargets(data);
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleOnSelectAudience = (
    flexibleIndex: number,
    target: AudienceSetTargetsData,
  ) => {
    let temp: AudienceSet = { ...formValues };

    let tempFlexibleSpec: AudienceSetTargetType[] = [
      ...(temp?.targets?.flexible_spec || []),
    ];

    tempFlexibleSpec[flexibleIndex] = {
      ...(tempFlexibleSpec?.[flexibleIndex] || {}),
      [target.type]: [
        ...(tempFlexibleSpec?.[flexibleIndex]?.[target.type] || []),
        target,
      ],
    };

    setFormValues({
      ...temp,
      targets: { ...temp.targets, flexible_spec: tempFlexibleSpec },
    });
  };

  const handleOnSelectTargetType = (
    e: any,
    targetType: { label: string; type: string; class?: string },
  ) => {
    let temp: string[] = [];
    if (e.target.checked) {
      temp = [...searchFilters, targetType.type];

      if (targetType.type === 'education_statuses') {
        setTargets([...targets, ...EDUCATION_STATUSES]);
        setFilteredTargets([...filteredTargets, ...EDUCATION_STATUSES]);
      }

      if (targetType.type === 'relationship_statuses') {
        setTargets([...targets, ...RELATIONSHIP_STATUSES]);
        setFilteredTargets([...filteredTargets, ...RELATIONSHIP_STATUSES]);
      }
    } else {
      temp = searchFilters.filter(
        (filter: string) => filter !== targetType.type,
      );

      if (targetType.type === 'education_statuses') {
        setTargets(
          targets.filter((target) => target.type !== 'education_statuses'),
        );
        setFilteredTargets(
          targets.filter((target) => target.type !== 'relationship_statuses'),
        );
      }

      if (targetType.type === 'relationship_statuses') {
        setTargets([...targets, ...RELATIONSHIP_STATUSES]);
        setFilteredTargets([...filteredTargets, ...RELATIONSHIP_STATUSES]);
      }
    }

    setSearchFilters(temp);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                formValues?.targets?.targeting_optimization === 'expansion_all'
              }
            />
          }
          label={
            <Fragment>
              <Box
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" sx={{ marginRight: '5px' }}>
                  Enable Advantage Detailed Targeting
                </Typography>

                <HtmlTooltip
                  disableInteractive
                  title={
                    <Fragment>
                      <Box component="div" sx={{ fontWeight: 'bold' }}>
                        Advantage detailed targeting
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        This expands your audience beyond your selections to
                        reach more people. We recommend this option because your
                        campaign will be using our most advanced ad technology
                        to help maximize performance.{' '}
                        <a
                          href="https://www.facebook.com/business/help/128066880933676"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Learn more about Advantage lookalike.
                        </a>
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        Because your audience is expanded through automation,
                        the estimated audience size will not reflect the total
                        number of Account Center accounts that meet the audience
                        criteria.
                      </Box>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon
                    sx={{ fontSize: '18px', color: '#096F4D' }}
                  />
                </HtmlTooltip>
              </Box>
            </Fragment>
          }
          value={
            formValues?.targets?.targeting_optimization === 'expansion_all'
          }
          onChange={(e: any) =>
            setFormValues({
              ...formValues,
              targets: {
                ...formValues?.targets,
                targeting_optimization: e.target.checked
                  ? 'expansion_all'
                  : 'none',
              },
            })
          }
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                formValues?.targets?.targeting_automation
                  ?.advantage_audience === 1
              }
            />
          }
          label={
            <Fragment>
              <Box
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" sx={{ marginRight: '5px' }}>
                  Enable Advantage+ Audience
                </Typography>

                <HtmlTooltip
                  title={
                    <Fragment>
                      <Box component="div" sx={{ fontWeight: 'bold' }}>
                        Advantage+ Audience
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        Advantage+ Audience on Facebook uses AI to optimize
                        audience targeting for your ads, aiming to reach people
                        most likely to respond and achieve your goals. This
                        feature can automatically expand your audience beyond
                        your initial selections if AI determines it will lead to
                        better performance. It's a hybrid approach that allows
                        you to provide some audience suggestions while still
                        leveraging Meta's AI to refine and potentially expand
                        the reach.{' '}
                        <a
                          href="https://www.facebook.com/business/help/273363992030035?id=1629569087788063"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Learn more about Advantage+ Audience.
                        </a>
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        Because your audience is expanded through automation,
                        the estimated audience size will not reflect the total
                        number of Account Center accounts that meet the audience
                        criteria.
                      </Box>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon
                    sx={{
                      fontSize: '18px',
                      color: '#096F4D',
                    }}
                  />
                </HtmlTooltip>
              </Box>
            </Fragment>
          }
          value={
            formValues?.targets?.targeting_automation?.advantage_audience === 1
          }
          onChange={(e: any) =>
            setFormValues({
              ...formValues,
              targets: {
                ...formValues?.targets,
                targeting_automation: {
                  ...formValues?.targets?.targeting_automation,
                  advantage_audience: e.target.checked ? 1 : 0,
                },
              },
            })
          }
        />
      </Grid>

      {formValues?.targets?.targeting_automation?.advantage_audience === 1 ? (
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'red' }}>
            Advantage+ Audience Enabled: You can select a minimum age between 18
            and 25, and a maximum age of 65 or above.
          </Typography>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          name="body"
          onChange={(e: ChangeEventType) => setSearchQuery(e.target.value)}
          InputLabelProps={{ shrink: true }}
          multiline
          label="Enter Search Terms"
          minRows={4}
          maxRows={100}
          placeholder={`Enter search terms separated by a new line eg.\nSearch Term 1\nSearch Term 2`}
        />
      </Grid>

      <Grid item xs={12} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
        <PrimaryButton
          title="Search Targets"
          type="button"
          handleOnClick={handleOnSearch}
          loading={searchLoading}
        />
      </Grid>

      <Grid item xs={12}>
        {TARGET_TYPES.map(
          (targetType: { label: string; type: string; class?: string }) => {
            const selected = searchFilters.includes(targetType.type);

            return (
              <FormControlLabel
                key={`target-type-${targetType.type}`}
                control={<Checkbox size="small" checked={selected} />}
                label={targetType.label}
                value={selected}
                onChange={(e: any) => handleOnSelectTargetType(e, targetType)}
              />
            );
          },
        )}
      </Grid>

      {searchLoading ? (
        <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
          <Spinner />
        </Grid>
      ) : targets.length > 0 ? (
        <>
          <Grid item xs={12}>
            <Divider>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', textAlign: 'center' }}
              >
                {filteredTargets.length} Results Found
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              id="search"
              name="search"
              label="Search"
              onChange={handleOnSearchLocation}
              value={search}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          {filteredTargets
            ?.slice(0, limit)
            ?.map((target: AudienceSetTargetsData) => {
              return (
                <Grid item xs={12}>
                  <AudienceList
                    target={target}
                    withAction
                    onSelectTarget={handleOnSelectAudience}
                    formValues={formValues}
                    search
                  />
                </Grid>
              );
            })}

          {limit < filteredTargets?.length ? (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
            >
              <PrimaryButton
                title="Load More"
                type="button"
                variant="outlined"
                handleOnClick={() => setLimit((prev) => prev + 25)}
                rounded
              />
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

export default DetailedAudienceSetForm;
